import {
  Target, Swords, Gamepad2, CalendarDays, ArrowUpRight, Shield, Flag,
} from 'lucide-react'
import HomeShell from './HomeShell'
import DiscordIcon from '@/components/site/DiscordIcon'
import Reveal from '@/components/site/Reveal'
import DiscordServerCard from '@/components/site/DiscordServerCard'
import RecentJoins from '@/components/site/RecentJoins'
import useDiscordInvite from '@/components/site/useDiscordInvite'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'
import { JOIN_SITE_URL } from '@/lib/site'

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
      {children}
    </h2>
  )
}

/**
 * turksletloose.com — the community front door.
 *
 * Order of the page is the order of the priorities: the community first, what
 * we play second and the competitive roster third. Recruitment is deliberately
 * the quietest thing here — the requirements, the steps and the form all live
 * on join.turksletloose.com, and this page only hands people over to it.
 *
 * The game-server row that used to open the page is gone: there is nothing to
 * announce, count down to or report a player count for yet, and a row that
 * says only "soon" is worse than no row. When a server does come up it starts
 * again from a clean section rather than from a stale countdown.
 *
 * **This page sells nothing.** The supporter band and the launch-offer strip
 * that used to sit above the hero were both taken out — not switched off, as
 * the rest of the membership is (`SUPPORTER_ENABLED` in lib/features.ts), but
 * removed from the front door. Turning that flag back on restores /supporter,
 * the nav links and the profile card; it does not put a band back here. If the
 * front page should carry one again, it is written again.
 */
export default function HomeLanding() {
  const { T } = useLanguage()
  const inviteUrl = useDiscordInvite()

  const games = [
    { icon: Swords,      title: T.home.games.wardogsTitle,  body: T.home.games.wardogsBody },
    { icon: Target,      title: T.home.games.shootersTitle, body: T.home.games.shootersBody },
    { icon: Gamepad2,    title: T.home.games.partyTitle,    body: T.home.games.partyBody },
    { icon: CalendarDays, title: T.home.games.nightsTitle,  body: T.home.games.nightsBody },
  ]

  const rosters = [
    { icon: Shield, title: T.home.competitive.mainTitle,    body: T.home.competitive.mainBody },
    { icon: Flag,   title: T.home.competitive.academyTitle, body: T.home.competitive.academyBody },
  ]

  return (
    <HomeShell sectionNav>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative">
        {/* One screen of place, then the page hands over to the map grid
            underneath. The gradient is a horizon rather than a cut — the image
            loses to the graphite before the section ends, so nothing draws a
            line across the page.

            Two photographs, not one: the crest plate on top fades away to the
            squad shot below it and back, on a slow 40s loop, and the pair
            drift through a gentle zoom while they do (`hero-crossfade` and
            `hero-zoom` in index.css). Both layers are always painted, so the
            swap is one opacity ramp — no image ever loads or unmounts
            mid-cycle.

            The zoom is on the wrapper around the two, so they scale as one and
            cannot drift apart mid-fade; the scrim is outside it and never
            moves, which is what keeps the contrast under the type constant.

            The crest plate is anchored high in its frame (`center 18%`) and
            the hero carries a tall minimum on wide screens for the same
            reason: this hero is far wider than the photograph, so a centred
            crop of a short band cut the emblem off at both ends. The height is
            what makes the whole crest fit; the offset is what centres it in
            what is left. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="hero-zoom absolute inset-0">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: "url('/dashboardbg.webp')" }}
            />
            <div
              className="hero-crossfade absolute inset-0 bg-cover bg-no-repeat"
              style={{
                backgroundImage: "url('/background.webp')",
                backgroundPosition: 'center 18%',
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/80 to-[#0b0b0d]" />
        </div>

        {/* Wordmark hard left, Discord card hard right, and the space between
            them left empty on purpose: the crest in the photograph is centred
            on the viewport, and this is what lets it stand in the gap rather
            than behind a paragraph. flex + justify-between rather than the
            grid that was here, because the two blocks should sit at the edges
            at whatever width the type happens to take, not in two columns
            measured from the middle. */}
        <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-5 pb-16 pt-20 md:pb-24 md:pt-28 lg:min-h-[78vh] lg:flex-row lg:items-center lg:justify-between lg:gap-20">
          <div className="min-w-0">
            {/* One line, always. The wordmark breaking across two lines reads
                as an accident rather than a lockup, so the size follows the
                viewport instead of the text following the box.

                It tops out at 4rem rather than the 4.5rem it was first set at:
                Tektur sets wide, the line is fifteen characters of it, and at
                that ceiling the wordmark ran all the way to the middle of the
                page — which is the part of the photograph the crest stands
                in. The badge that briefly sat above this line is gone; the
                ceiling stays, because the crest is the reason for it. */}
            <h1 className="whitespace-nowrap font-display text-[clamp(1.9rem,5.2vw,4rem)] font-extrabold uppercase leading-[0.95] tracking-tight">
              {T.home.heroTitle}
            </h1>

            {/* Subtitle rather than a chip above the wordmark: the name lands
                first, and the rule carries the accent so the type stays bone. */}
            <p className="mt-4 flex items-center gap-3 font-display text-[11px] font-bold uppercase tracking-[0.3em] text-foreground/60 md:text-[13px]">
              <span className="h-px w-8 shrink-0 bg-primary" />
              {T.home.heroKicker}
            </p>

            {/* Narrower than the wordmark above it, so the paragraph does not
                reach further into the middle than the type it belongs to. */}
            <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80 md:text-lg">
              {T.home.heroLead}
            </p>
          </div>

          {/* Live server card, at the right edge on wide screens and below the
              headline on narrow ones. It carries the only call to action the
              hero needs, so the button row that used to sit under the lead is
              gone rather than repeating the same invite twice. Fixed width
              rather than a share of the row: it is a card, and it should not
              grow into the gap the crest needs. */}
          <Reveal delay={160} className="w-full lg:w-[340px] lg:shrink-0">
            <DiscordServerCard />
          </Reveal>
        </div>
      </section>

      {/* ── Community ────────────────────────────────────────────────────── */}
      {/* First band under the hero now that the server row is gone, and the
          start of the clear/glass alternation the rest of the page keeps:
          glass here, clear at "what we play", glass at the roster, and so on
          down to the closing CTA. The map behind the page is what makes the
          pair read — soft under the glass and sharp between, so the rhythm is
          the background going in and out of focus rather than two flat greys.
          Adding a section in the middle means checking the one under it still
          alternates. */}
      <section id="community" className="scroll-mt-14 border-t border-white/10 bg-black/25 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-6xl px-5 py-16 md:py-24">
          {/* Photograph beside the paragraph, framed in the same panel as every
              card on the page rather than bleeding to the edge. The claim here
              is "people come for whoever is in the voice channel", and a squad
              moving together is the picture of it. */}
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)]">
            <Reveal className="max-w-2xl">
              <SectionTitle>{T.home.community.title}</SectionTitle>
              <p className="mt-6 text-base leading-relaxed text-foreground/80">{T.home.community.body}</p>
            </Reveal>

            <Reveal delay={160}>
              <div className="relative overflow-hidden rounded-md border border-white/12 bg-[#0c0c0f]">
                <img
                  src="/banner_2.webp"
                  alt=""
                  loading="lazy"
                  className="block aspect-[16/10] w-full object-cover"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0c0c0f] via-[#0c0c0f]/20 to-transparent"
                />
              </div>
            </Reveal>
          </div>

          {/* Between the paragraph and the button, and outside the max-w-2xl
              Reveal above: the strip is the evidence for the claim the
              paragraph makes, so it belongs before the invitation rather than
              after it — and it runs the full width of the section, because
              that width is the distance it has to slide along. */}
          <Reveal delay={120}>
            <RecentJoins />
          </Reveal>

          {inviteUrl && (
            <Reveal delay={200} className="mt-9">
              <a href={inviteUrl} target="_blank" rel="noreferrer" className="inline-block">
                <Button variant="outline" className="gap-3">
                  <DiscordIcon />
                  {T.home.community.cta}
                </Button>
              </a>
            </Reveal>
          )}
        </div>
      </section>

      {/* ── What we play ─────────────────────────────────────────────────── */}
      {/* Third treatment of the same three photographs: here one is the
          section's ground rather than an object in it. Held well back — the
          cards are the content, the picture only says the section is about
          playing. The gradient starts and ends on the page colour so the band
          has no edges of its own. */}
      <section id="games" className="relative scroll-mt-14 overflow-hidden border-t border-white/10">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <img
            src="/banner_3.webp"
            alt=""
            loading="lazy"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d] via-[#0b0b0d]/75 to-[#0b0b0d]" />
        </div>

        <div className="relative mx-auto w-full max-w-6xl px-5 py-16 md:py-24">
          <Reveal><SectionTitle>{T.home.games.title}</SectionTitle></Reveal>
          <div className="mt-8 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {games.map((g, i) => (
              <Reveal key={g.title} delay={100 + i * 110} className="bg-card/80 p-5">
                <g.icon className="h-5 w-5 text-primary" />
                <h3 className="mt-3.5 font-display text-base font-bold tracking-tight">{g.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{g.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Competitive side ─────────────────────────────────────────────── */}
      <section id="competitive" className="scroll-mt-14 border-t border-white/10 bg-black/25 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-6xl px-5 py-16 md:py-24">
          {/* The one place on the page where a heading sits on top of a
              picture. It suits this section: the roster is the part of the
              community that lines up and goes somewhere, and the shot is of
              exactly that. Two scrims — one across, one up from the floor —
              because the type has to hold over both the dark figure on the
              left and the sunset behind it. */}
          <Reveal>
            <div className="relative overflow-hidden rounded-md border border-white/12 bg-[#0c0c0f]">
              <img
                src="/banner_1.webp"
                alt=""
                loading="lazy"
                className="block h-[240px] w-full object-cover object-center md:h-[320px]"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0b0b0d] via-[#0b0b0d]/70 to-transparent"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                <SectionTitle>{T.home.competitive.title}</SectionTitle>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground/75">
                  {T.home.competitive.lead}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {rosters.map((r, i) => (
              <Reveal key={r.title} delay={100 + i * 120}>
                <div className="h-full rounded-md border border-white/10 bg-card/80 p-6">
                  <r.icon className="h-5 w-5 text-primary" />
                  <h3 className="mt-3.5 font-display text-lg font-bold uppercase tracking-tight">{r.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={220} className="mt-7">
            <a href={JOIN_SITE_URL}>
              <Button variant="outline" className="gap-2">
                {T.home.competitive.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </a>
          </Reveal>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      {/* Clear, following the glass roster above it — the last step of the
          alternation the page has kept since the community band. */}
      <section className="border-t border-white/10">
        <Reveal className="mx-auto flex w-full max-w-6xl flex-col items-center gap-5 px-5 py-16 text-center md:py-24">
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
            {T.home.closing.title}
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
            {T.home.closing.body}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {inviteUrl && (
              <a href={inviteUrl} target="_blank" rel="noreferrer">
                <Button size="lg" className="gap-3 font-semibold transition-transform active:scale-95">
                  <DiscordIcon />
                  {T.home.discordJoinCta}
                </Button>
              </a>
            )}
            <a href={JOIN_SITE_URL}>
              <Button size="lg" variant="outline" className="gap-2">
                {T.home.joinCta}
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </a>
          </div>
        </Reveal>
      </section>
    </HomeShell>
  )
}
